/**
 * domains/ai/thesis2/validator.ts — MER Algo Footprints v7.1H
 *
 * Parses and validates the raw AI JSON response (nested contract).
 * Repair logic operates on the nested sub-objects.
 * Change detection compares fully assembled StructuredThesis objects.
 */

import {
  StructuredThesis, ThesisState, ActionPosture, ThesisBias,
  ChangeType, ChangeDetection,
} from './types';
import { validateRawAIResponse, validateFinalThesis } from './schema';
export { validateFinalThesis };

const VALID_STATES:   ThesisState[]   = ['ACTIONABLE','DEVELOPING','WAIT','HIGH_RISK','INVALIDATED'];
const VALID_POSTURES: ActionPosture[] = ['LONG','SHORT','NEUTRAL','REDUCE_RISK','NO_TRADE'];
const VALID_BIASES:   ThesisBias[]    = ['bullish','bearish','neutral','mixed'];

// ─── Parse raw AI output ──────────────────────────────────────────────────────

export type ParseResult =
  | { ok: true;  raw: Record<string, unknown> }
  | { ok: false; error: string };

export function parseAIJSON(rawText: string): ParseResult {
  let cleaned = rawText
    .replace(/^```json\s*/m, '')
    .replace(/^```\s*/m,     '')
    .replace(/```\s*$/m,     '')
    .trim();

  try {
    const parsed = JSON.parse(cleaned);
    if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
      return { ok: true, raw: parsed as Record<string, unknown> };
    }
    return { ok: false, error: 'Parsed value is not a JSON object' };
  } catch {
    const match = cleaned.match(/\{[\s\S]*\}/);
    if (match) {
      try {
        const parsed = JSON.parse(match[0]);
        return { ok: true, raw: parsed as Record<string, unknown> };
      } catch (e2: any) {
        return { ok: false, error: `JSON extraction failed: ${e2.message}` };
      }
    }
    return { ok: false, error: 'No JSON object found in AI response' };
  }
}

// ─── Field validation — delegates to Zod schema (nested contract) ─────────────

export interface FieldValidationResult {
  valid:  boolean;
  errors: string[];
}

export function validateFields(raw: Record<string, unknown>): FieldValidationResult {
  // Delegates entirely to Zod schema — single source of truth
  const result = validateRawAIResponse(raw);
  if (result.ok) return { valid: true, errors: [] };
  return { valid: false, errors: result.errors };
}

// ─── Sanitize a string array ───────────────────────────────────────────────────

function sanitizeStringArray(val: unknown): string[] {
  if (!Array.isArray(val)) return [];
  return val
    .filter(el => el !== null && el !== undefined)
    .map(el => (typeof el === 'string' ? el : String(el)))
    .filter(el => el.trim().length > 0)
    .slice(0, 10);
}

// ─── Repair — operates on the nested raw response shape ──────────────────────
//
// The raw AI response is nested:
//   { entry_plan: { entry_trigger, confirmation_needed, ... },
//     risk_plan:  { invalidation_level, invalidation_reason, primary_risk_note },
//     targets:    { target_1, target_2, target_logic },
//     management: { max_hold_note, uncertainty_disclosure, ... },
//     evidence:   { smc_read, ..., supporting_factors, conflict_factors, key_drivers },
//     change_detection: { what_changed, ... },
//     ... top-level: thesis_state, action_posture, bias, conviction, actionability,
//                    setup_classification, setup_name, summary, decision }
//
// Repair must operate on the same shape — never on flat top-level keys.

function repairObject(obj: unknown, defaults: Record<string, unknown>): Record<string, unknown> {
  const base: Record<string, unknown> =
    (obj !== null && typeof obj === 'object' && !Array.isArray(obj))
      ? { ...(obj as Record<string, unknown>) }
      : {};
  for (const [k, v] of Object.entries(defaults)) {
    if (base[k] === undefined || base[k] === null || String(base[k]).trim() === '') {
      base[k] = v;
    }
  }
  return base;
}

export function repairFields(raw: Record<string, unknown>): Record<string, unknown> {
  const r = { ...raw };

  // ── Top-level scalars ─────────────────────────────────────────────────────
  if (!VALID_STATES.includes(r.thesis_state as ThesisState))      r.thesis_state    = 'WAIT';
  if (!VALID_POSTURES.includes(r.action_posture as ActionPosture)) r.action_posture  = 'NO_TRADE';
  if (!VALID_BIASES.includes(r.bias as ThesisBias))               r.bias            = 'neutral';
  r.conviction    = Math.max(0, Math.min(100, Number(r.conviction)    || 0));
  r.actionability = Math.max(0, Math.min(100, Number(r.actionability) || 0));
  if (!r.setup_classification) r.setup_classification = 'unknown';
  if (!r.setup_name)           r.setup_name           = 'Unknown';
  if (!r.summary)              r.summary              = 'Analysis incomplete — AI response was repaired deterministically.';
  if (!r.decision)             r.decision             = 'No actionable decision — thesis repaired from incomplete AI response.';

  // ── Semantic consistency ──────────────────────────────────────────────────
  if (r.thesis_state === 'INVALIDATED') r.action_posture = 'NO_TRADE';
  if (r.thesis_state === 'ACTIONABLE' && r.action_posture === 'NO_TRADE') r.thesis_state = 'DEVELOPING';
  if (r.action_posture === 'LONG'  && r.bias === 'bearish') r.action_posture = 'NEUTRAL';
  if (r.action_posture === 'SHORT' && r.bias === 'bullish') r.action_posture = 'NEUTRAL';

  // ── Nested sub-objects ────────────────────────────────────────────────────
  r.entry_plan = repairObject(r.entry_plan, {
    entry_trigger:       'Entry trigger not specified — await AI restoration',
    entry_price_hint:    null,
    confirmation_needed: 'Confirmation criteria not specified — confirm via price action',
    timing_note:         null,
  });

  r.risk_plan = repairObject(r.risk_plan, {
    invalidation_level:  'Review manually — not provided by AI',
    invalidation_reason: 'AI did not specify invalidation reason',
    primary_risk_note:   'Risk assessment incomplete — treat with caution',
  });

  r.targets = repairObject(r.targets, {
    target_1:    null,
    target_2:    null,
    target_logic: 'Targets not specified — use structural levels from engine evidence',
  });

  r.management = repairObject(r.management, {
    max_hold_note:          'Exit at session end or invalidation — repaired default',
    scale_note:             null,
    re_entry_note:          null,
    uncertainty_disclosure: 'AI output was repaired deterministically — high uncertainty, treat as preliminary only',
  });

  // ── Evidence: arrays sanitized at nested path ─────────────────────────────
  const ev = repairObject(r.evidence, {
    smc_read:       'Not provided',
    regime_read:    'Not provided',
    liquidity_read: 'Not provided',
    trap_read:      'Not provided',
    gamma_read:     'N/A — no options data',
    setup_read:     'Not provided',
    supporting_factors: [],
    conflict_factors:   [],
    key_drivers:        ['Insufficient data'],
  });
  ev.supporting_factors = sanitizeStringArray(ev.supporting_factors);
  ev.conflict_factors   = sanitizeStringArray(ev.conflict_factors);
  ev.key_drivers        = sanitizeStringArray(ev.key_drivers);
  if ((ev.key_drivers as string[]).length === 0) ev.key_drivers = ['Insufficient data'];
  r.evidence = ev;

  // ── Change detection: what_changed sanitized at nested path ──────────────
  const cd = repairObject(r.change_detection, {
    change_type:      'NO_MATERIAL_CHANGE',
    material_changes: [],
    what_changed:     [],
  });
  cd.what_changed     = sanitizeStringArray(cd.what_changed);
  cd.material_changes = sanitizeStringArray(cd.material_changes);
  r.change_detection  = cd;

  return r;
}

// ─── Price plausibility check ─────────────────────────────────────────────────

const MAX_PRICE_DEVIATION = 0.30;

export function validatePriceField(
  field:    string | null | undefined,
  curPrice: number,
  _name:    string,
): string | null {
  if (!field) return null;
  const n = parseFloat(String(field).replace(/[^0-9.-]/g, ''));
  if (isNaN(n) || n <= 0) return null;
  if (Math.abs(n - curPrice) / curPrice > MAX_PRICE_DEVIATION) return null;
  return String(n.toFixed(2));
}

// ─── Change detection — receives full StructuredThesis objects ────────────────

export function detectThesisChanges(
  current: StructuredThesis,
  prior:   StructuredThesis | null,
): ChangeDetection {
  if (!prior) {
    return {
      changed:               true,
      change_type:           'UPGRADE',
      material_changes:      ['Initial thesis generated'],
      previous_thesis_state: null,
      previous_posture:      null,
      previous_bias:         null,
      conviction_delta:      null,
    };
  }

  const changes: string[] = [];
  let change_type: ChangeType = 'NO_MATERIAL_CHANGE';

  // State change
  if (current.thesis_state !== prior.thesis_state) {
    changes.push(`State: ${prior.thesis_state} → ${current.thesis_state}`);
    if (current.thesis_state === 'INVALIDATED') {
      change_type = 'INVALIDATION';
    } else if (
      (prior.thesis_state === 'WAIT' || prior.thesis_state === 'DEVELOPING') &&
       current.thesis_state === 'ACTIONABLE'
    ) {
      change_type = 'UPGRADE';
    } else if (prior.thesis_state === 'ACTIONABLE' && current.thesis_state !== 'ACTIONABLE') {
      change_type = 'DOWNGRADE';
    }
  }

  // Bias flip (directional only)
  const priorDir = prior.bias === 'bullish' || prior.bias === 'bearish';
  const currDir  = current.bias === 'bullish' || current.bias === 'bearish';
  if (priorDir && currDir && prior.bias !== current.bias) {
    changes.push(`Bias flipped: ${prior.bias} → ${current.bias}`);
    change_type = 'BIAS_FLIP';
  }

  // Posture change
  if (current.action_posture !== prior.action_posture) {
    changes.push(`Posture: ${prior.action_posture} → ${current.action_posture}`);
    if (change_type === 'NO_MATERIAL_CHANGE') change_type = 'DOWNGRADE';
  }

  // Conviction delta (≥10 points)
  const convDelta = current.conviction - prior.conviction;
  if (Math.abs(convDelta) >= 10) {
    changes.push(`Conviction ${convDelta > 0 ? '+' : ''}${convDelta} (${prior.conviction} → ${current.conviction})`);
    if (change_type === 'NO_MATERIAL_CHANGE')
      change_type = convDelta > 0 ? 'UPGRADE' : 'DOWNGRADE';
  }

  // Risk flags
  const priorRisky = prior.risk_plan.trap_conflict || prior.risk_plan.regime_conflict;
  const currRisky  = current.risk_plan.trap_conflict || current.risk_plan.regime_conflict;
  if (!priorRisky && currRisky) {
    changes.push('Risk flags activated');
    if (change_type === 'NO_MATERIAL_CHANGE') change_type = 'RISK_INCREASE';
  } else if (priorRisky && !currRisky) {
    changes.push('Risk flags cleared');
    if (change_type === 'NO_MATERIAL_CHANGE') change_type = 'RISK_DECREASE';
  }

  // Actionability threshold
  if (current.actionability >= 70 && prior.actionability < 70) {
    changes.push('Actionability crossed 70%');
    if (change_type === 'NO_MATERIAL_CHANGE') change_type = 'TRIGGER_APPROACHING';
  }

  // Provenance: fallback → real AI
  if (prior.audit.validation_status === 'FALLBACK' &&
      current.audit.validation_status !== 'FALLBACK') {
    changes.push('AI analysis restored (was deterministic fallback)');
    if (change_type === 'NO_MATERIAL_CHANGE') change_type = 'UPGRADE';
  }

  return {
    changed:               changes.length > 0,
    change_type,
    material_changes:      changes,
    previous_thesis_state: prior.thesis_state,
    previous_posture:      prior.action_posture,
    previous_bias:         prior.bias,
    conviction_delta:      convDelta,
  };
}
